import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { Link } from 'react-router-dom';

const SessionVerificationPayoutCycleArticle = () => (
    <Box>
        <Typography variant="h3" component="h1" fontWeight={700} gutterBottom sx={{ fontSize: { xs: '1.65rem', md: '2rem' } }}>
            How Session Verification Changes Your Payout Cycle
        </Typography>
        <Typography color="text.secondary" fontWeight={600} paragraph sx={{ fontStyle: 'italic' }}>
            Verified sessions. Calculated payouts. No reconciliation.
        </Typography>
        <Typography color="text.secondary" paragraph>
            <strong>TL;DR:</strong> Session verification changes what a payout is calculated from. Most home rehab centres close
            the month on a number both parties have accepted, not one either party can prove. When every session has a
            verified record created at point of delivery, the payout figure comes from confirmed data. The reconciliation step
            doesn&apos;t get shorter. It disappears. This is what changes in your payout cycle when session verification is
            built into daily operations.
        </Typography>
        <Typography color="text.secondary" paragraph>
            Session verification changes what the payout number is calculated from. Instead of a figure both parties have
            negotiated into agreement, the number comes from confirmed delivery records created at point of service. The
            reconciliation step doesn&apos;t get shorter. It disappears.
        </Typography>
        <Typography color="text.secondary" paragraph>
            If you&apos;ve worked through the payout dispute problem, tried stricter reporting, more detailed WhatsApp
            confirmations, better spreadsheets, and the disputes keep coming, the issue isn&apos;t the process. It&apos;s the
            input.
        </Typography>
        <Typography color="text.secondary" paragraph>
            Payout calculations that start from self-reported session data will always require reconciliation. The
            therapist&apos;s count and the centre&apos;s count are two separate records being matched after the fact. When they
            don&apos;t match, the cycle ends in negotiation. That&apos;s not a process failure. It&apos;s what happens when the
            input isn&apos;t verified.
        </Typography>
        <Typography color="text.secondary" paragraph>
            Session verification changes the input. When a verified record is created at point of delivery, the payout figure
            doesn&apos;t come from what a therapist reported. It comes from what was confirmed. That&apos;s a different
            calculation, and it produces a different month-end.
        </Typography>

        <Typography variant="h2" component="h2" fontWeight={700} sx={{ fontSize: '1.35rem', mt: 4, mb: 1.5 }}>
            What a verified session record is, and what it is not
        </Typography>
        <Typography color="text.secondary" paragraph>
            A verified session record is a structured record of who delivered a session, when, where, and what was assessed and
            treated, created at point of delivery, not submitted after the fact.
        </Typography>
        <Typography color="text.secondary" paragraph>
            That distinction matters because most platforms don&apos;t create this. They record that a session was scheduled
            and mark it complete based on manual input. That&apos;s a schedule with a status update. It tells you a therapist
            said the session happened. It does not tell you that it did.
        </Typography>
        <Typography color="text.secondary" paragraph>
            A verified session record is independent of therapist self-reporting. The required fields are completed on-site, at
            the time of delivery. The record is timestamped and stored independently of any message thread or spreadsheet.
            What the therapist submits later doesn&apos;t create the record. The record already exists.
        </Typography>

        <Typography variant="h2" component="h2" fontWeight={700} sx={{ fontSize: '1.35rem', mt: 4, mb: 1.5 }}>
            What the payout cycle looks like without session verification
        </Typography>
        <Typography color="text.secondary" paragraph>
            Month-end arrives. Payout calculation begins. Therapists submit their session tallies over WhatsApp, on a shared
            sheet, or verbally. The admin cross-references those counts against whatever records exist. A group chat. A call
            log. A spreadsheet no one has updated consistently.
        </Typography>
        <Typography color="text.secondary" paragraph>
            The numbers don&apos;t match. They rarely do. The admin chases the therapist for clarification. The therapist
            provides detail from memory. A figure is agreed on, not because either party can prove it, but because the cycle
            has to close.
        </Typography>
        <Typography color="text.secondary" paragraph>
            The cost at each stage: admin or finance lead time spent on manual cross-referencing, often hours per cycle.
            Financial leakage on sessions disputed and settled at a lower figure. Therapist trust erodes when legitimate
            claims are questioned without a shared record to reference. A month-end number that is an approximation, not a
            fact.
        </Typography>

        <Typography variant="h2" component="h2" fontWeight={700} sx={{ fontSize: '1.35rem', mt: 4, mb: 1.5 }}>
            How session verification works in practice
        </Typography>
        <Typography color="text.secondary" paragraph>
            The workflow looks like this:
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 2, color: 'text.secondary' }}>
            <Box component="li" sx={{ mb: 1 }}>
                The therapist arrives at the patient&apos;s home.
            </Box>
            <Box component="li" sx={{ mb: 1 }}>
                The session is delivered.
            </Box>
            <Box component="li" sx={{ mb: 1 }}>
                Before leaving, the therapist completes the structured record on-site — patient, time, location, on-site proof
                of delivery, clinical documentation with required fields.
            </Box>
            <Box component="li" sx={{ mb: 1 }}>
                The record is timestamped and stored independently.
            </Box>
            <Box component="li" sx={{ mb: 1 }}>
                That record links directly to the payout calculation at month-end.
            </Box>
        </Box>
        <Typography color="text.secondary" paragraph>
            There is no separate confirmation step. There is no reconciliation process that depends on matching the
            therapist&apos;s submission against something else. The session happened, the record exists, the payout reflects
            it.
        </Typography>

        <Typography variant="h2" component="h2" fontWeight={700} sx={{ fontSize: '1.35rem', mt: 4, mb: 1.5 }}>
            What changes in the payout cycle
        </Typography>
        <Typography color="text.secondary" paragraph>
            Without verified sessions, the payout cycle ends in compromise. With them, it ends in calculation.
        </Typography>
        <Typography color="text.secondary" paragraph>
            The admin opens the system at month-end. The sessions are there, each with a confirmed record behind it. The
            payout figures are calculated from that data. If a therapist queries their figure, the record for each session is
            available - who was seen, when, what was documented. The conversation is short because the data is already there.
        </Typography>
        <Typography color="text.secondary" paragraph>
            The reconciliation step disappears because there is nothing to reconcile. Session data and payout data are no
            longer collected separately and matched after the fact. They come from the same source: the verified record created
            at delivery.
        </Typography>
        <Typography color="text.secondary" paragraph>
            The downstream effects are specific. No rehab financial leakage on disputed sessions. No admin hours spent chasing
            documentation that may not exist. No month-end figure that both parties accepted without being able to prove.
        </Typography>

        <Typography variant="h2" component="h2" fontWeight={700} sx={{ fontSize: '1.35rem', mt: 4, mb: 1.5 }}>
            Why clinic software cannot replicate this
        </Typography>
        <Typography color="text.secondary" paragraph>
            Most practice management tools record that a session was scheduled and mark it complete. That&apos;s not
            verification. It&apos;s a schedule with a status update.
        </Typography>
        <Typography color="text.secondary" paragraph>
            The architectural assumption built into clinic software is that sessions happen inside a facility - recorded by
            staff at the front desk, billed from a fixed location, with the therapist and the patient in the same building as
            the records system. That assumption is wrong for home visits.
        </Typography>
        <Typography color="text.secondary" paragraph>
            A home visit happens at a patient&apos;s house. The therapist is the only person present. The record has to be
            created by the therapist, on-site, at the time of delivery. Clinic software wasn&apos;t designed for that workflow.
            Adding a mobile app to a clinic PMS doesn&apos;t change the underlying assumption. The record still depends on the
            therapist marking something complete rather than completing a verified record at point of delivery.
        </Typography>
        <Typography color="text.secondary" paragraph>
            This is an architectural gap, not a missing feature. A verified session record that links directly to payout
            calculation requires a system built around field service governance from the ground up.
        </Typography>
        <Typography color="text.secondary" paragraph>
            Related:{' '}
            <Link to="/blog/why-clinic-software-fails-home-visit-teams">Why Clinic Software Fails for Home Visit Teams</Link>.
        </Typography>

        <Typography variant="h2" component="h2" fontWeight={700} sx={{ fontSize: '1.35rem', mt: 4, mb: 1.5 }}>
            What meDZhub does differently
        </Typography>
        <Typography color="text.secondary" paragraph>
            meDZhub creates a verified session record at point of delivery. The therapist completes the required fields on-site
            - patient, time, location, clinical documentation. That record is the input to both the documentation trail and the
            payout calculation. There is no separate data entry step. There is no matching process at month-end.
        </Typography>
        <Typography color="text.secondary" paragraph>
            The payout number is not calculated from a schedule. It is calculated from confirmed deliveries. Every session in
            the calculation has a verified record behind it. Sessions without a verified record don&apos;t appear in the
            payout. The result is therapist payout automation that doesn&apos;t depend on anyone&apos;s memory or goodwill.
        </Typography>
        <Typography color="text.secondary" paragraph>
            Centres on meDZhub report zero payout disputes. The platform currently tracks over 1,000 sessions monthly across 35
            active therapists.
        </Typography>
        <Typography color="text.secondary" paragraph>
            Related:{' '}
            <Link to="/blog/payout-disputes-home-rehab-fix">
                Payout Disputes in Home Rehab: Can Your System Fix Them?
            </Link>
            .
        </Typography>

        <Typography variant="h2" component="h2" fontWeight={700} sx={{ fontSize: '1.35rem', mt: 4, mb: 1.5 }}>
            What month-end looks like when every session is verified
        </Typography>
        <Typography color="text.secondary" paragraph>
            The admin opens the system. The sessions are there, each with a confirmed record. The payout figures are calculated
            from that data. If a therapist queries their figure, the record is reviewed together - here&apos;s each session,
            here&apos;s what was captured, here&apos;s where the count comes from.
        </Typography>
        <Typography color="text.secondary" paragraph>
            No days spent chasing documentation. No conversations that end in compromise. No figure that both parties
            accepted without being able to prove.
        </Typography>
        <Typography color="text.secondary" paragraph>
            Month-end becomes a short administrative task rather than a week-long investigation. For centre owners who have
            spent months managing disputes and reconciliation, that shift matters - not only because it saves time, but also
            because it removes a recurring source of friction that compounds in ways that are hard to reverse.
        </Typography>
        <Typography color="text.secondary" paragraph>
            Related:{' '}
            <Link to="/blog/when-home-therapy-teams-grow-what-breaks-at-scale">
                When Home Therapy Teams Grow: What Breaks at Scale
            </Link>
            .
        </Typography>
        <Typography color="text.secondary" paragraph sx={{ mt: 2 }}>
            If you&apos;re evaluating whether session verification can fix your payout cycle, the demo is the fastest way to
            see the specific linkage in practice.
        </Typography>
        <Button component={Link} to="/demo" variant="contained" sx={{ mt: 1, mb: 4 }} data-gtm="Blog-session-verification-demo">
            Book a demo
        </Button>

        <Typography variant="h2" component="h2" fontWeight={700} sx={{ fontSize: '1.35rem', mt: 2, mb: 2 }}>
            Frequently asked questions
        </Typography>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            How does session verification work in practice for a home therapy team?
        </Typography>
        <Typography color="text.secondary" paragraph>
            The therapist completes a structured record on-site at the end of each session - patient, time, location, clinical
            documentation. That record is created at point of delivery, not submitted later. It becomes the direct input to
            documentation and payout calculation with no manual step in between.
        </Typography>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mt: 2 }}>
            What exactly links a verified session to a therapist payout?
        </Typography>
        <Typography color="text.secondary" paragraph>
            The verified session record is the payout input. Sessions with a confirmed record appear in the calculation.
            Sessions without one don&apos;t. There is no separate matching process. The record and the payout figure come from
            the same data set.
        </Typography>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mt: 2 }}>
            What happens to month-end reconciliation when every session has a verified record?
        </Typography>
        <Typography color="text.secondary" paragraph>
            It disappears. Reconciliation exists because session data and payout data are collected separately and matched after
            the fact. When the verified session record is the direct input to payout calculation, there is nothing to
            reconcile.
        </Typography>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mt: 2 }}>
            How does this differ from asking therapists to submit more detailed reports?
        </Typography>
        <Typography color="text.secondary" paragraph>
            Detailed reports are still self-reported. They depend on the therapist&apos;s memory and are created after the
            fact. A verified session record is created at point of delivery with required fields completed on-site. The
            difference isn&apos;t the detail. It&apos;s when and how the record is created.
        </Typography>
    </Box>
);

export default SessionVerificationPayoutCycleArticle;
